import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Avatar, Box, Card, CardContent, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { STATUS_UPDATE_ACTION } from '../../../../constants/jobOrderConstants';
import selectUser from '../../../../store/selectors/appSelector';
import theme from '../../../../theme';
import { BLACK } from '../../../../theme/colorConstants';
import { epochToTimeInReadableFormat } from '../../../../utils';
import latestGpsStatus from '../../../../views/jobOrderDetail/RosterPanel/EmployeeRosterCards/latestGpsStatus';
import RosterMetaRow from '../../../../views/jobOrderDetail/RosterPanel/EmployeeRosterCards/RosterMetaRow';
import {
  redesignActionButtonsSx,
  redesignClockSx,
  redesignHoursSx,
  redesignNameSx,
  redesignRowTotalSx,
  redesignTimeHeaderSx,
} from '../../../JobOrderCard/redesignRosterType';
import TimekeepingEditDrawer from '../../../TimeKeepingEditDrawer-deprecated/TimeKeepingEditDrawer';
import WorkerNameLink from '../../../WorkerNameLink/WorkerNameLink';
import {
  showApproveBtn,
  showFinalizeBtn,
  showResolveBtn,
  showUnFinalizeBtn,
} from '../../ActiveRosterView/SubComponents/statusUpdateButtons';
import showTimecardEditButton from '../../ActiveRosterView/SubComponents/timecardEditButton';
import { getHours, getTotalHours } from '../../helperFunctions';

const RedesignClock = ({ value }) => {
  if (!value || value === '--') {
    return value || '--';
  }
  const parts = String(value).trim().split(' ');
  const period = parts[parts.length - 1];
  if (period !== 'AM' && period !== 'PM') {
    return value;
  }
  return (
    <>
      <Box component="span" sx={{ fontSize: '12px' }}>
        {parts.slice(0, -1).join(' ')}
      </Box>
      <Box component="span" sx={{ fontSize: '10px', ml: '4px' }}>
        {period}
      </Box>
    </>
  );
};

RedesignClock.propTypes = {
  value: PropTypes.string,
};

const useStyles = makeStyles({
  card: {
    cursor: 'pointer',
    boxShadow: 'none',
    '& .content': {
      display: 'flex',
      alignItems: 'center',
      padding: '0',
      justifyContent: 'space-between',
      '& .header': {
        display: 'flex',
        alignItems: 'center',
        '& .avatar': {
          marginRight: '10px',
          width: '34px',
          height: '34px',
        },
        '& .employee': {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          '& .name': {
            display: 'flex',
            fontSize: '16px',
            lineHeight: '16px',
            ...theme.jobOrders.detail.roster.employeeRosterCard.employeeName,
          },
          '& .checkInStatus': {
            display: 'flex',
            fontWeight: '300',
            fontSize: '14px',
            lineHeight: '16px',
            ...theme.jobOrders.detail.roster.employeeRosterCard.checkInStatus,
          },
        },
      },
    },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    paddingTop: '8px',
    '&:last-child': {
      paddingBottom: 0,
    },
    '& .header': {
      display: 'flex',
      justifyContent: 'space-between',
      '& .text': {
        flex: 1,
        textAlign: 'center',
        fontSize: '10px',
        lineHeight: '24px',
        textTransform: 'uppercase',
        ...theme.jobOrders.detail.roster.employeeRosterCard.headerText,
      },
    },
    '& .punches': {
      display: 'flex',
      '& .time': {
        flex: 1,
        textAlign: 'center',
        fontSize: '12px',
        lineHeight: '24px',
        ...theme.jobOrders.detail.roster.employeeRosterCard.punchTime,
      },
    },
    '& .total': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& .text': {
        textAlign: 'center',
        fontFamily: 'Barlow',
        fontSize: '12px',
        fontWeight: '700',
        lineHeight: '24px',
      },
    },
    '& .actionButtons': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      width: '100%',
      paddingTop: '4px',
    },
  },
});

const EmployeeRosterCard = ({
  corporationName,
  placement,
  clearTimeCardError,
  handleTimecardEditSubmit,
  onStatusUpdate,
  timecardIsUpdating,
  timecardUpdateError,
  shiftName,
  redesign = false,
}) => {
  const classes = useStyles();
  const user = useSelector(selectUser);

  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  const userRole = user?.role;

  const currentPunches = placement?.timecard?.current?.punches;
  const currentBreakTime = placement?.timecard?.current?.breakTime;

  const CheckInStatuses = {
    PRESENT: 'present',
    SENT_HOME: 'sent-home',
    NO_SHOW: 'no-show',
  };
  const checkInStatus = placement?.timecard?.checkIn?.status;
  const gpsStatus = latestGpsStatus(placement);
  const checkInStatusText =
    (checkInStatus === CheckInStatuses.NO_SHOW && LanguageConverter('jobOrder.markedAsNoShow')) ||
    (checkInStatus === CheckInStatuses.SENT_HOME && LanguageConverter('jobOrder.markedAsSentHome'));

  const handleOnEdit = () => {
    setIsEditDrawerOpen(true);
  };
  const editDrawerInitData = {
    corporation: corporationName,
    shiftName,
    start: placement?.start,
    end: placement?.end,
    punches:
      // Set the default start/end dates to match the dates of the expected start/end of the shift

      currentPunches?.length > 0
        ? currentPunches.map((punch) => ({
            ...punch,
            jobStart: placement?.start,
            jobEnd: placement?.end,
          }))
        : [
            {
              jobStart: placement?.start,
              jobEnd: placement?.end,
            },
          ],
    breakDelta: currentBreakTime / 60 / 1000 || 0,
  };

  const handleOnEditClose = useCallback(() => {
    clearTimeCardError();
    setIsEditDrawerOpen(false);
  }, [clearTimeCardError]);

  const handleOnEditSubmit = useCallback(
    ({ punches, breakDelta }) => {
      const data = {
        placementId: placement?._id,
        breakDelta: parseInt(breakDelta * 60 * 1000, 10),
        punches: punches.map((punch) => ({
          in: punch?.in?.stamp,
          out: punch?.out?.stamp,
        })),
      };
      handleTimecardEditSubmit(data);
      handleOnEditClose();
      return true;
    },
    [handleTimecardEditSubmit, placement?._id, handleOnEditClose]
  );

  const handleOnStatusUpdate = (status) => () =>
    onStatusUpdate({ placementId: placement?._id, status });

  const renderClock = (stamp) => {
    if (!stamp) {
      return '--';
    }
    const formatted = epochToTimeInReadableFormat(stamp);
    if (!redesign) {
      return formatted;
    }
    return <RedesignClock value={formatted} />;
  };

  return (
    <Card className={classes.card}>
      <CardContent className="content">
        <div className="header">
          <Avatar src={placement?.profilePic} alt={placement?.candidateName} className="avatar" />
          <div className="employee">
            <WorkerNameLink
              name={placement?.candidateName}
              userId={placement?.candidate}
              agencyId={placement?.agency}
              variant={redesign ? undefined : 'h6'}
              className={redesign ? undefined : 'name'}
              sx={redesign ? redesignNameSx : undefined}
            />
            <RosterMetaRow
              isPriorityResourcePool={placement?.isPriorityResourcePool}
              firstTimeWorkingThisCustomer={placement?.firstTimeWorkingThisCustomer}
              gpsStatus={gpsStatus}
              showTime
              compactBadges
              card={redesign}
            />
            <Typography variant="body2" className="checkInStatus">
              {checkInStatusText}
            </Typography>
          </div>
        </div>
      </CardContent>
      <CardContent className={classes.details}>
        <Box sx={{ border: 1, borderRadius: '5px' }}>
          <Box
            className="header"
            sx={{
              borderBottom: 1,
              ...(redesign && { backgroundColor: BLACK[10] }),
            }}
          >
            <Typography
              className={redesign ? undefined : 'text'}
              sx={redesign ? redesignTimeHeaderSx : { textAlign: 'center' }}
            >
              {LanguageConverter('buttonText.clockIn')}{' '}
            </Typography>
            <Typography
              className={redesign ? undefined : 'text'}
              sx={redesign ? redesignTimeHeaderSx : undefined}
            >
              {LanguageConverter('buttonText.clockOut')}
            </Typography>
            <Typography
              className={redesign ? undefined : 'text'}
              sx={redesign ? redesignTimeHeaderSx : undefined}
            >
              {LanguageConverter('jobOrder.hours')}
            </Typography>
            <Typography
              className={redesign ? undefined : 'text'}
              sx={redesign ? redesignTimeHeaderSx : undefined}
            >
              {LanguageConverter('jobOrder.totalHours')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', direction: 'row' }}>
            <Box sx={{ width: '250px' }}>
              {currentPunches?.length > 0 ? (
                currentPunches?.map((punch) => (
                  <Box className="punches">
                    <Typography
                      className={redesign ? undefined : 'time'}
                      sx={redesign ? redesignClockSx : undefined}
                    >
                      {renderClock(punch?.in?.stamp)}
                    </Typography>
                    <Typography
                      className={redesign ? undefined : 'time'}
                      sx={redesign ? { ...redesignClockSx, borderLeft: 1 } : { borderLeft: 1 }}
                    >
                      {renderClock(punch?.out?.stamp)}
                    </Typography>
                    <Typography
                      className={redesign ? undefined : 'time'}
                      sx={
                        redesign
                          ? { ...redesignHoursSx, borderLeft: 1, borderRight: 1 }
                          : { borderLeft: 1, borderRight: 1 }
                      }
                    >
                      {punch?.out?.stamp ? getHours(punch?.in?.stamp, punch?.out?.stamp) : '0.00'}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Box className="punches">
                  <Typography
                    className={redesign ? undefined : 'time'}
                    sx={redesign ? redesignClockSx : undefined}
                  >
                    --
                  </Typography>
                  <Typography
                    className={redesign ? undefined : 'time'}
                    sx={redesign ? { ...redesignClockSx, borderLeft: 1 } : { borderLeft: 1 }}
                  >
                    --
                  </Typography>
                  <Typography
                    className={redesign ? undefined : 'time'}
                    sx={
                      redesign
                        ? { ...redesignHoursSx, borderLeft: 1, borderRight: 1 }
                        : { borderLeft: 1, borderRight: 1 }
                    }
                  >
                    --
                  </Typography>
                </Box>
              )}
            </Box>
            <Box className="total" sx={{ width: '85px' }}>
              <Typography
                className={redesign ? undefined : 'text'}
                sx={redesign ? redesignRowTotalSx : undefined}
              >
                {getTotalHours(placement?.timecard?.current?.totalTime)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box className="actionButtons" sx={redesign ? redesignActionButtonsSx : undefined}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {showTimecardEditButton(
              handleOnEdit,
              placement?.start,
              placement?.timecard?.status,
              userRole
            )}
            {showApproveBtn(
              placement?.timecard?.status,
              placement?.end,
              userRole,
              handleOnStatusUpdate(STATUS_UPDATE_ACTION.APPROVE)
            )}
            {showResolveBtn(
              placement?.timecard?.status,
              placement?.end,
              userRole,
              handleOnStatusUpdate(STATUS_UPDATE_ACTION.RESOLVE)
            )}
            {showFinalizeBtn(
              placement?.timecard?.status,
              placement?.end,
              userRole,
              handleOnStatusUpdate(STATUS_UPDATE_ACTION.FINALIZE)
            )}
            {showUnFinalizeBtn(
              placement?.timecard?.status,
              placement?.end,
              userRole,
              handleOnStatusUpdate(STATUS_UPDATE_ACTION.UNFINALIZE)
            )}
          </Box>
        </Box>
        <TimekeepingEditDrawer
          isDrawerOpen={isEditDrawerOpen}
          initialData={editDrawerInitData}
          onSubmit={handleOnEditSubmit}
          onClose={handleOnEditClose}
          timecardIsUpdating={timecardIsUpdating}
          errorMessage={timecardUpdateError}
        />
      </CardContent>
    </Card>
  );
};

EmployeeRosterCard.propTypes = {
  placement: PropTypes.shape({
    _id: PropTypes.string,
    checkInStatus: PropTypes.string,
    candidateName: PropTypes.string,
    candidate: PropTypes.string,
    agency: PropTypes.string,
    isPriorityResourcePool: PropTypes.bool,
    firstTimeWorkingThisCustomer: PropTypes.bool,
    gpsData: PropTypes.shape({
      onsite: PropTypes.bool,
      timestamp: PropTypes.number,
    }),
    gpsPings: PropTypes.arrayOf(
      PropTypes.shape({
        onsite: PropTypes.bool,
        timestamp: PropTypes.number,
      })
    ),
    timecard: PropTypes.shape({
      current: PropTypes.shape({
        punches: PropTypes.arrayOf(PropTypes.shape({})),
        totalTime: PropTypes.number,
        breakTime: PropTypes.number,
      }),
      checkIn: PropTypes.shape({ status: PropTypes.string }),
      status: PropTypes.string,
    }),
    start: PropTypes.number,
    end: PropTypes.number,
    profilePic: PropTypes.string,
    state: PropTypes.shape({ userName: PropTypes.string }),
  }),
  corporationName: PropTypes.string,
  clearTimeCardError: PropTypes.func,
  shiftName: PropTypes.string,
  handleTimecardEditSubmit: PropTypes.func,
  onStatusUpdate: PropTypes.func,
  timecardIsUpdating: PropTypes.bool,
  timecardUpdateError: PropTypes.string,
  redesign: PropTypes.bool,
};

export default EmployeeRosterCard;
